import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Box } from '@mui/material';
import { NavPills, Typography } from '@totalsoft/rocket-ui';
import EngineScoringClassificationCategoryListContainer from '../categories/EngineScoringClassificationCategoryListContainer';
import { Dashboard, FormatListBulleted, PlaylistAddCheck } from '@mui/icons-material';
import EngineScoringClassificationHeaderContainer from './EngineScoringClassificationHeaderContainer';
import EngineScoringClassificationAttributeListContainer from '../attributes/EngineScoringClassificationAttributeListContainer';
import EngineScoringClassificationFinancialIndicatorListContainer from '../financialIndicators/EngineScoringClassificationFinancialIndicatorListContainer';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';

const EngineScoringClassificationListItemComponent = ({
  engineScoringId,
  engineClassificationIndex,
  scoringClassification,
  collectionValidation,
  isReadOnly,
  dispatch,
  onSave,
  scoringCategoryOptions,
  dictionaryApplicationPage,
  dictionaryScoringResponseState,
  engineAttributeOptions,
  scoringFinancialIndicatorOptions,
  useAllAttributes,
  evaluatedAttributes
}) => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const categoriesAreValid = collectionValidation[engineClassificationIndex].categories |> isValid;
  const categoriesErrors = collectionValidation[engineClassificationIndex].categories |> getErrors;

  const attributesAreValid = collectionValidation[engineClassificationIndex].attributes |> isValid;
  const attributesErrors = collectionValidation[engineClassificationIndex].attributes |> getErrors;

  const financialIndicatorsAreValid = collectionValidation[engineClassificationIndex].financialIndicators |> isValid;
  const financialIndicatorsErrors = collectionValidation[engineClassificationIndex].financialIndicators |> getErrors;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12}>
        <EngineScoringClassificationHeaderContainer
          scoringClassification={scoringClassification}
          engineClassificationIndex={engineClassificationIndex}
          dispatch={dispatch}
          isReadOnly={isReadOnly}
          onSave={onSave}
          collectionValidation={collectionValidation}
          dictionaryApplicationPage={dictionaryApplicationPage}
          dictionaryScoringResponseState={dictionaryScoringResponseState}
        />
      </Grid>
      {!categoriesAreValid && (
        <Grid item xs={12}>
          <Box component='span' fontWeight={'fontWeightBold'}>
            <Typography color='error'>{t('EngineScoring.EngineClassifications.EngineCategory.Title') + ': '}</Typography>
          </Box>
          {categoriesErrors}
          {'.'}
        </Grid>
      )}
      {!attributesAreValid && (
        <Grid item xs={12}>
          <Box component='span' fontWeight={'fontWeightBold'}>
            <Typography color='error'>{t('EngineScoring.EngineClassifications.EngineAttribute.Title') + ': '}</Typography>
          </Box>
          {attributesErrors}
          {'.'}
        </Grid>
      )}
      {!financialIndicatorsAreValid && (
        <Grid item xs={12}>
          <Box component='span' fontWeight={'fontWeightBold'}>
            <Typography color='error'>{t('EngineScoring.EngineClassifications.FinancialIndicator.Title') + ': '}</Typography>
          </Box>
          {financialIndicatorsErrors}
          {'.'}
        </Grid>
      )}
      <Grid item xs={12} sm={12}>
        <NavPills
          color='info'
          active={active}
          onChange={(_event, active) => {
            setActive(active);
          }}
          orientation='vertical'
          fullWidth={true}
          tabPanelProps={{ width: 1 }}
          tabs={[
            {
              label: t('EngineScoring.EngineClassifications.EngineCategory.Title'),
              icon: <Dashboard />,
              props: { fullWidth: true },
              content: (
                <EngineScoringClassificationCategoryListContainer
                  engineScoringId={engineScoringId}
                  categories={scoringClassification.categories}
                  isReadOnly={isReadOnly}
                  dispatch={dispatch}
                  engineClassificationIndex={engineClassificationIndex}
                  validation={collectionValidation[engineClassificationIndex].categories}
                  scoringCategoryOptions={scoringCategoryOptions}
                />
              )
            },
            {
              label: t('EngineScoring.EngineClassifications.EngineAttribute.Title'),
              icon: <FormatListBulleted />,
              content: (
                <EngineScoringClassificationAttributeListContainer
                  engineScoringId={engineScoringId}
                  attributes={scoringClassification.attributes}
                  isReadOnly={isReadOnly}
                  dispatch={dispatch}
                  engineClassificationIndex={engineClassificationIndex}
                  validation={collectionValidation[engineClassificationIndex].attributes}
                  engineAttributeOptions={engineAttributeOptions}
                  useAllAttributes={useAllAttributes}
                  evaluatedAttributes={evaluatedAttributes}
                />
              )
            },
            {
              label: t('EngineScoring.EngineClassifications.FinancialIndicator.Title'),
              icon: <PlaylistAddCheck />,
              content: (
                <EngineScoringClassificationFinancialIndicatorListContainer
                  engineScoringId={engineScoringId}
                  financialIndicators={scoringClassification.financialIndicators}
                  isReadOnly={isReadOnly}
                  dispatch={dispatch}
                  engineClassificationIndex={engineClassificationIndex}
                  validation={collectionValidation[engineClassificationIndex].financialIndicators}
                  scoringFinancialIndicatorOptions={scoringFinancialIndicatorOptions}
                />
              )
            }
          ]}
        />
      </Grid>
    </Grid>
  );
};
EngineScoringClassificationListItemComponent.propTypes = {
  engineScoringId: PropTypes.number.isRequired,
  scoringClassification: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  engineClassificationIndex: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  collectionValidation: PropTypes.object.isRequired,
  scoringCategoryOptions: PropTypes.array.isRequired,
  dictionaryApplicationPage: PropTypes.array.isRequired,
  dictionaryScoringResponseState: PropTypes.array.isRequired,
  engineAttributeOptions: PropTypes.array.isRequired,
  scoringFinancialIndicatorOptions: PropTypes.array.isRequired,
  useAllAttributes: PropTypes.bool.isRequired,
  evaluatedAttributes: PropTypes.array.isRequired
};

export default EngineScoringClassificationListItemComponent;
